import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button, IconButton } from '../ui';
import { PHASE_KINDS, makePhase, normalizePhases } from '../../lib/database';
import { kindSegments, kindId, kindShortLabel, kindChipStyle, isComboKind, kindHasLabor, phaseLaborPricing, phasePackPricing, placePhasePricing } from '../../lib/phaseKinds';

// ============================================================================
// PhaseCalendarStrip — SEQUENCE PIPELINE view for planning an estimate's
// phases. Renders a horizontal pipeline of compact drag-to-reorder chips.
// No dates, no edit drawer. Crew sizes are inherited from the pricing
// inputs and visible on each chip. The dispatch board assigns dates.
//
// Props:
//   phases, onChange, disabled, anchorDate (ignored, API compat)
// ============================================================================

// Chip colors are generated from the kind's segments (phaseKinds.js,
// 2026-08-26 combo refactor) — singles get their base palette, combos a
// split-diagonal gradient across their segments. The old hand-made
// KIND_STYLES map is gone; any combo the user builds gets a coherent
// style automatically.

const PersonIcon = ({ color }) => (
    <svg width="9" height="10" viewBox="0 0 8 9" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
        <circle cx="4" cy="2.5" r="1.8" fill={color} />
        <path d="M0.5 8.5C0.5 6.6 2.1 5.2 4 5.2C5.9 5.2 7.5 6.6 7.5 8.5" stroke={color} strokeWidth="1" fill="none" />
    </svg>
);

export default function PhaseCalendarStrip({ phases = [], onChange, disabled = false, anchorDate = null }) {
    const emit = (next) => { if (typeof onChange === 'function') onChange(normalizePhases(next)); };

    // Drag state (pointer-driven — see "Drag mechanics" below).
    // { idx, ghost: {x, y, w, h}, target: { mode: 'merge', idx } | { mode: 'reorder', slot, bar: {x, top, h} } | { mode: 'none' } }
    const [drag, setDrag] = useState(null);
    const dragRef = useRef(null);        // live drag bookkeeping (no re-render)
    const pipelineRef = useRef(null);    // the chip row — overlay bar is positioned inside it
    const chipRefs = useRef(new Map());  // sequence → chip element
    const [addOpen, setAddOpen] = useState(false);
    // In-place kind swap (Jensen 2026-08-25): clicking a chip's kind label
    // opens a picker that changes JUST the kind — crew, duration, date, and
    // per-day pricing all survive. Before this, swapping LOAD → LD/DRV meant
    // delete + re-add, which reset the crew to 0 with no visible way to fix it.
    const [kindMenu, setKindMenu] = useState(null); // { seq, top, left } | null
    const kindMenuRef = useRef(null);
    // "+ Phase" expands IN PLACE into a left-to-right picker tray (Jaylen
    // 2026-09-17: the dropdown had to be reopened for every phase). The tray
    // stays open while you click PACK, LOAD, DRIVE… — each click appends a
    // chip to its left — and shrinks back to the button on Done / ✕ /
    // outside click / Escape. An empty planner opens with the tray out.
    const addRef = useRef(null);   // the tray (or the button) wrapper
    const sorted = [...phases].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
    const trayOpen = addOpen || (sorted.length === 0 && !disabled);

    // -- Mutators -----------------------------------------------------------
    const handleField = (seq, field, value) => {
        const next = phases.map(p => p.sequence === seq ? { ...p, [field]: value } : p);
        emit(next);
    };

    const handleRemove = (seq) => {
        emit(phases.filter(p => p.sequence !== seq));
    };

    const handleAdd = (kind) => {
        // Seed the new phase's crew from the last chip in the pipeline —
        // a brand-new phase almost always belongs to the same trip, and a
        // silent crew of 0 was how "replace LOAD with LD/DRV" lost its
        // crew requirement (Jensen 2026-08-25).
        const last = sorted[sorted.length - 1];
        const next = [...phases, makePhase({ kind, sequence: phases.length + 1, duration_days: 1, crew_size: last?.crew_size || 0, trucks_needed: last?.trucks_needed || '' })];
        emit(next);
        setAddOpen(true); // tray stays out — keep clicking
    };

    // Swap a phase's kind IN PLACE — everything else (crew, duration, date,
    // time, per-day hours/rate, linked move_jobs) is preserved.
    const handleKindSwap = (seq, kind) => {
        emit(phases.map(p => p.sequence === seq ? { ...p, kind } : p));
        setKindMenu(null);
    };

    const openKindMenu = (e, seq) => {
        e.stopPropagation();
        if (disabled) return;
        if (kindMenu?.seq === seq) { setKindMenu(null); return; }
        const r = e.currentTarget.getBoundingClientRect();
        setKindMenu({ seq, top: r.bottom + 4, left: r.left });
    };

    const handleCrewChange = (seq, delta) => {
        const next = phases.map(p => {
            if (p.sequence !== seq) return p;
            return { ...p, crew_size: Math.max(0, (Number(p.crew_size) || 0) + delta) };
        });
        emit(next);
    };

    const handleDurationChange = (seq, delta) => {
        const next = phases.map(p => {
            if (p.sequence !== seq) return p;
            return { ...p, duration_days: Math.max(1, (p.duration_days || 1) + delta) };
        });
        emit(next);
    };

    // ── Combo merge / split (Jaylen 2026-08-26) ────────────────────────
    // Two chips merge into ONE day whose kind is their segments in
    // pipeline order (drive + unload + return → 'drive+unload+return').
    // Triggered by dropping a chip onto another chip's CENTER, or by
    // clicking the → connector between two chips. Splitting a combo
    // (kind menu → "Split into N days") turns each segment back into its
    // own consecutive phase.
    const ZERO_PRICING = { min: 0, max: 0, rate: 0 };
    const sumPricing = (x, y) => ({ min: x.min + y.min, max: x.max + y.max, rate: x.rate || y.rate || 0 });
    const mergeByIndex = (i, j) => {
        const a = sorted[Math.min(i, j)];
        const b = sorted[Math.max(i, j)];
        if (!a || !b || a === b) return;
        const segs = [...kindSegments(a.kind), ...kindSegments(b.kind)]
            .filter((s, k, arr) => arr.indexOf(s) === k); // dedupe (load+load → load)
        const merged = {
            ...a,
            kind: kindId(segs),
            duration_days: 1, // a combo IS one day by definition
            crew_size: Math.max(Number(a.crew_size) || 0, Number(b.crew_size) || 0),
            trucks_needed: a.trucks_needed || b.trucks_needed || '',
            date: a.date || b.date || null,
            time: a.time || b.time || null,
            dispatcher_notes: [a.dispatcher_notes, b.dispatcher_notes].filter(Boolean).join(' · '),
            // Per-day pricing: LABOR hours add up with labor hours and
            // PACKING hours with packing hours — the two sides never blend
            // (2026-09-10, Alwin BBM-2609-018: pack 3h×$125 merged onto a
            // move day used to become "Day 1 labor 3h × $125"). Each side
            // keeps its first non-zero rate; placePhasePricing puts them
            // in the right slots for the merged kind.
            ...placePhasePricing(kindId(segs), sumPricing(phaseLaborPricing(a), phaseLaborPricing(b)),
                                               sumPricing(phasePackPricing(a), phasePackPricing(b))),
            // Keep a's move_job_ids — b's board rows become orphans that
            // the next Push to Dispatch cleans up.
        };
        emit(sorted.filter(p => p !== b).map(p => (p === a ? merged : p)));
    };

    // Reorder one segment inside a combo (menu ↑/↓). Sequences don't
    // change, so the open menu stays valid.
    const reorderSegment = (seq, i, dir) => {
        const p = sorted.find(x => x.sequence === seq);
        if (!p) return;
        const segs = [...kindSegments(p.kind)];
        const j = i + dir;
        if (j < 0 || j >= segs.length) return;
        [segs[i], segs[j]] = [segs[j], segs[i]];
        emit(phases.map(x => x.sequence === seq ? { ...x, kind: kindId(segs) } : x));
    };

    // Split ONE segment out of a combo into its own neighboring day —
    // first segment splits off before the combo, anything else after.
    // The combo keeps its date/hours/board links; the new day starts
    // dateless with the same crew.
    const splitOffSegment = (seq, i) => {
        const p = sorted.find(x => x.sequence === seq);
        if (!p) return;
        const segs = kindSegments(p.kind);
        if (segs.length < 2) return;
        const seg = segs[i];
        const rest = segs.filter((_, k) => k !== i);
        // Pricing follows its segment (2026-09-10): a pack split off takes
        // the packing pricing with it; the combo keeps the labor pricing.
        // A labor/travel segment splitting off starts unpriced (as before)
        // and the combo keeps everything.
        const labor = phaseLaborPricing(p);
        const pack = phasePackPricing(p);
        const remaining = {
            ...p,
            kind: kindId(rest),
            ...placePhasePricing(kindId(rest), labor, seg === 'pack' ? ZERO_PRICING : pack),
        };
        const splitOut = {
            ...p,
            kind: seg,
            duration_days: 1,
            date: null,
            time: null,
            ...placePhasePricing(seg, ZERO_PRICING, seg === 'pack' ? pack : ZERO_PRICING),
            move_job_ids: [],
            move_job_id: null,
        };
        const idx = sorted.indexOf(p);
        const next = i === 0
            ? [...sorted.slice(0, idx), splitOut, remaining, ...sorted.slice(idx + 1)]
            : [...sorted.slice(0, idx), remaining, splitOut, ...sorted.slice(idx + 1)];
        emit(next);
        setKindMenu(null); // sequences shift — close rather than point at the wrong chip
    };

    const splitPhase = (seq) => {
        const p = sorted.find(x => x.sequence === seq);
        if (!p) return;
        const segs = kindSegments(p.kind);
        if (segs.length < 2) return;
        // Pricing follows its segment (2026-09-10): the pack day gets the
        // packing pricing, the FIRST labor day gets the labor pricing,
        // everything else starts unpriced.
        const labor = phaseLaborPricing(p);
        const pack = phasePackPricing(p);
        const firstLaborIdx = Math.max(0, segs.findIndex(s => kindHasLabor(s))); // no labor seg → day 1 keeps it
        const parts = segs.map((s, i) => ({
            ...p,
            kind: s,
            duration_days: 1,
            date: i === 0 ? p.date : null,   // later days get dated on the board
            time: i === 0 ? p.time : null,
            ...placePhasePricing(s, i === firstLaborIdx ? labor : ZERO_PRICING, s === 'pack' ? pack : ZERO_PRICING),
            move_job_ids: i === 0 ? p.move_job_ids : [],
            move_job_id: i === 0 ? p.move_job_id : null,
        }));
        const idx = sorted.indexOf(p);
        emit([...sorted.slice(0, idx), ...parts, ...sorted.slice(idx + 1)]);
        setKindMenu(null);
    };

    // -- Drag mechanics (Jaylen 2026-09-17: "works, but hit or miss") -------
    // Rewritten from native HTML5 drag/drop to pointer events. What made
    // the old one feel random:
    //   • only the chips were drop targets — a drop in the gap or on the →
    //     arrow (where "put it here" naturally lands) did nothing;
    //   • the insertion bar was a flex item, so showing it shifted every
    //     chip to the right of it by ~6px — the chip under the cursor moved,
    //     the merge/reorder zone flipped, and the target flickered;
    //   • the merge zone was the middle 40% of a wide chip, so dragging
    //     PAST a chip to reorder often merged instead, and the intent was
    //     re-read from raw geometry at drop time (could differ from what was
    //     highlighted);
    //   • dropping on a chip's left vs right half gave the same result.
    // Now: chip rectangles are snapshotted when the drag starts (nothing
    // that renders during a drag changes layout — the bar is an absolute
    // overlay, the → arrows stay put), the pointer resolves to either a
    // MERGE target (over a chip's inner third, with hysteresis so it doesn't
    // flicker at the edge) or a REORDER slot (the nearest gap in the row
    // under the pointer — before/after decided by the chip's centre), the
    // ghost follows the pointer from where it was grabbed, a hint line says
    // exactly what a drop will do, and drop applies the highlighted target.
    // Escape cancels. Works with touch too (pointer events).
    const DRAG_THRESHOLD = 6; // px before a press becomes a drag (clicks on the chip still work)

    const measureChips = () => {
        const wrap = pipelineRef.current?.getBoundingClientRect();
        if (!wrap) return null;
        const rects = [];
        sorted.forEach((p, idx) => {
            const el = chipRefs.current.get(p.sequence);
            if (!el) return;
            const r = el.getBoundingClientRect();
            rects.push({ idx, left: r.left - wrap.left, top: r.top - wrap.top, right: r.right - wrap.left, bottom: r.bottom - wrap.top, cx: (r.left + r.right) / 2 - wrap.left, cy: (r.top + r.bottom) / 2 - wrap.top, w: r.width, h: r.height });
        });
        return rects;
    };

    // Rows of the wrapped pipeline: chips whose vertical centres sit within
    // half a chip height of each other. Flex-wrap keeps sequence order, so
    // each row is a contiguous idx range.
    const groupRows = (rects) => {
        const rows = [];
        for (const r of rects) {
            const row = rows[rows.length - 1];
            if (row && Math.abs(row.cy - r.cy) < r.h / 2) { row.chips.push(r); row.top = Math.min(row.top, r.top); row.bottom = Math.max(row.bottom, r.bottom); }
            else rows.push({ cy: r.cy, top: r.top, bottom: r.bottom, chips: [r] });
        }
        return rows;
    };

    const resolveTarget = (x, y, rects, dragIdx, prev) => {
        // 1. Merge: pointer over another chip's inner zone. Hysteresis —
        //    enter at the inner 40% (0.30–0.70), leave only past 0.20 / 0.80.
        const under = rects.find(r => r.idx !== dragIdx && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom);
        if (under) {
            const fx = (x - under.left) / Math.max(1, under.w);
            const stickingToMerge = prev?.mode === 'merge' && prev.idx === under.idx;
            const edge = stickingToMerge ? 0.20 : 0.30;
            if (fx > edge && fx < 1 - edge) return { mode: 'merge', idx: under.idx };
        }
        // 2. Reorder: the nearest gap in the row under the pointer.
        const rows = groupRows(rects);
        if (rows.length === 0) return { mode: 'none' };
        const row = rows.find(r => y >= r.top - 10 && y <= r.bottom + 10)
            || rows.reduce((best, r) => Math.abs(r.cy - y) < Math.abs(best.cy - y) ? r : best, rows[0]);
        const after = row.chips.find(c => c.cx > x);           // first chip whose centre is right of the pointer
        const slot = after ? after.idx : row.chips[row.chips.length - 1].idx + 1;
        if (slot === dragIdx || slot === dragIdx + 1) return { mode: 'none' }; // would land where it already is
        const before = row.chips.filter(c => c.cx <= x).pop();
        const barX = after && before ? (before.right + after.left) / 2
            : after ? after.left - 5
            : before.right + 5;
        return { mode: 'reorder', slot, bar: { x: barX, top: row.top, h: row.bottom - row.top } };
    };

    const endDrag = useCallback(() => {
        const d = dragRef.current;
        if (d?.el && d.pointerId != null) { try { d.el.releasePointerCapture(d.pointerId); } catch { /* already released */ } }
        dragRef.current = null;
        setDrag(null);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
    }, []);

    const handlePointerDown = (e, idx) => {
        if (disabled || e.button !== 0) return;
        // Presses on the chip's own controls (kind label, steppers, ×) are
        // clicks, never drags.
        if (e.target.closest('button, input, select, textarea, a')) return;
        dragRef.current = { idx, startX: e.clientX, startY: e.clientY, active: false, el: e.currentTarget, pointerId: e.pointerId, rects: null, grabDx: 0, grabDy: 0, target: null };
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* unsupported */ }
    };

    const handlePointerMove = (e) => {
        const d = dragRef.current;
        if (!d) return;
        if (!d.active) {
            if (Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < DRAG_THRESHOLD) return;
            const rects = measureChips();
            if (!rects) return;
            const own = rects.find(r => r.idx === d.idx);
            const wrap = pipelineRef.current.getBoundingClientRect();
            d.active = true;
            d.rects = rects;
            // Compact ghost (label · days · crew) — hangs just right of the
            // pointer so it never hides the chip you're aiming at.
            d.grabDx = -10;
            d.grabDy = own.h / 2;
            d.ownW = own.w; d.ownH = own.h;
            document.body.style.cursor = 'grabbing';
            document.body.style.userSelect = 'none';
            setKindMenu(null); setAddOpen(false);
        }
        e.preventDefault();
        const wrap = pipelineRef.current?.getBoundingClientRect();
        if (!wrap) return;
        const x = e.clientX - wrap.left, y = e.clientY - wrap.top;
        const target = resolveTarget(x, y, d.rects, d.idx, d.target);
        d.target = target;
        setDrag({ idx: d.idx, ghost: { x: x - d.grabDx, y: y - d.grabDy, w: d.ownW, h: d.ownH }, target });
    };

    const handlePointerUp = (e) => {
        const d = dragRef.current;
        if (!d) return;
        if (!d.active) { dragRef.current = null; return; } // it was a click
        e.preventDefault();
        const { idx, target } = d;
        endDrag();
        if (!target || target.mode === 'none') return;
        if (target.mode === 'merge') { mergeByIndex(idx, target.idx); return; }
        const arr = [...sorted];
        const [moved] = arr.splice(idx, 1);
        arr.splice(target.slot > idx ? target.slot - 1 : target.slot, 0, moved);
        emit(arr.map((p, i) => ({ ...p, sequence: i + 1 })));
    };

    // Escape cancels an in-flight drag.
    const dragging = !!drag;
    useEffect(() => {
        if (!dragging) return;
        const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); endDrag(); } };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [dragging, endDrag]);

    // What the drop will do — shown under the pipeline while dragging.
    const dragHint = (() => {
        if (!drag) return null;
        const moving = sorted[drag.idx];
        if (!moving) return null;
        if (drag.target?.mode === 'merge') {
            const other = sorted[drag.target.idx];
            const lo = drag.idx < drag.target.idx ? moving : other, hi = lo === moving ? other : moving;
            const segs = [...kindSegments(lo.kind), ...kindSegments(hi.kind)].filter((x, k, arr) => arr.indexOf(x) === k);
            return { tone: 'merge', text: `Drop to combine ${kindShortLabel(moving.kind)} + ${kindShortLabel(other.kind)} into one day → ${kindShortLabel(kindId(segs))}` };
        }
        if (drag.target?.mode === 'reorder') {
            const s = drag.target.slot;
            const after = s > 0 ? sorted[s - 1 === drag.idx ? s - 2 : s - 1] : null;
            const before = s < sorted.length ? sorted[s === drag.idx ? s + 1 : s] : null;
            return { tone: 'reorder', text: `Drop to move ${kindShortLabel(moving.kind)} ${before ? `before ${kindShortLabel(before.kind)}` : `to the end`}${after && before ? ` (after ${kindShortLabel(after.kind)})` : ''}` };
        }
        return { tone: 'none', text: `Drag ${kindShortLabel(moving.kind)} between chips to reorder · onto a chip to combine · Esc to cancel` };
    })();

    // Close the kind-swap menu on outside click / scroll / resize
    React.useEffect(() => {
        if (!kindMenu) return;
        const handler = (e) => {
            if (kindMenuRef.current?.contains(e.target)) return;
            setKindMenu(null);
        };
        document.addEventListener('mousedown', handler);
        const onScrollResize = () => setKindMenu(null);
        window.addEventListener('scroll', onScrollResize, true);
        window.addEventListener('resize', onScrollResize);
        return () => {
            document.removeEventListener('mousedown', handler);
            window.removeEventListener('scroll', onScrollResize, true);
            window.removeEventListener('resize', onScrollResize);
        };
    }, [kindMenu]);

    // Tray shrinks back on outside click / Escape (only when it was opened
    // by hand — an empty planner keeps it out).
    React.useEffect(() => {
        if (!addOpen) return;
        const onDown = (e) => { if (!addRef.current?.contains(e.target)) setAddOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setAddOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
    }, [addOpen]);

    const totalDuration = sorted.reduce((sum, p) => sum + Math.max(1, p.duration_days || 1), 0);

    return (
        <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
            {/* ── Header ──────────────────────────────────────────────────── */}
            <div className="flex items-center justify-between gap-2">
                <div>
                    <h4 className="text-xs font-bold text-slate-800">Phase Planner</h4>
                    <p className="text-[10px] text-slate-400">
                        Drag a chip between others to reorder, onto a chip to combine · Dates are set on the dispatch board
                    </p>
                </div>
                {sorted.length > 0 && (
                    <div className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-lg whitespace-nowrap">
                        {sorted.length} phase{sorted.length !== 1 ? 's' : ''} · {totalDuration} day{totalDuration !== 1 ? 's' : ''} total
                    </div>
                )}
            </div>

            {/* ── Add phases — pinned under the header (Jaylen 2026-09-17:
                the tray used to sit after the last chip and walked right
                with every add). `+ Phase` expands in place into a left-to-
                right picker that stays out while you click; Done / outside
                click / Escape shrink it back. New chips land below. */}
            {!disabled && (
                <div ref={addRef} className="flex items-center">
                    {!trayOpen ? (
                        <Button variant="secondary" size="sm" onClick={() => setAddOpen(true)}>
                            + Phase
                        </Button>
                    ) : (
                        <div className="flex flex-wrap items-center gap-1 pl-2 pr-1 py-1 rounded-lg border border-dashed border-slate-300 bg-slate-50"
                            role="group" aria-label="Add phases">
                            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mr-0.5">Add</span>
                            {/* Base kinds only (2026-08-26 combo refactor) —
                                combo days are built by merging chips. */}
                            {PHASE_KINDS.map(k => {
                                const kStyle = kindChipStyle(k.key);
                                return (
                                    <button key={k.key} type="button"
                                        onClick={() => handleAdd(k.key)}
                                        title={`Add a ${k.label.toLowerCase()} day`}
                                        className="h-7 px-2 rounded-md border font-black uppercase tracking-wider text-[9.5px] whitespace-nowrap hover:brightness-95 active:scale-95 transition-[transform,filter] duration-100"
                                        style={{ background: kStyle.bg, color: kStyle.color, borderColor: kStyle.border }}>
                                        {kindShortLabel(k.key)}
                                    </button>
                                );
                            })}
                            {sorted.length > 0 && (
                                <button type="button" onClick={() => setAddOpen(false)}
                                    className="h-7 px-2 ml-0.5 rounded-md text-[10px] font-bold text-slate-600 hover:bg-slate-200"
                                    aria-label="Done adding phases">
                                    Done
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ── Pipeline ───────────────────────────────────────────────── */}
            <div ref={pipelineRef} className={`relative flex flex-wrap items-center gap-1 ${sorted.length === 0 ? 'hidden' : ''}`}>
                {/* Insertion bar — an OVERLAY, so showing it never shifts the
                    chips under the pointer (the old inline bar did, and the
                    target flickered). */}
                {drag?.target?.mode === 'reorder' && (
                    <div className="absolute w-[3px] rounded-full bg-indigo-500 pointer-events-none z-10"
                        style={{ left: drag.target.bar.x - 1.5, top: drag.target.bar.top - 2, height: drag.target.bar.h + 4 }} />
                )}
                {/* Ghost — the chip you're holding, following the pointer from
                    the point you grabbed it. */}
                {drag && (() => {
                    const g = sorted[drag.idx]; if (!g) return null;
                    const gs = kindChipStyle(g.kind);
                    return (
                        <div className="absolute pointer-events-none z-20 flex items-center gap-1.5 px-2.5 rounded-lg border-2 shadow-xl"
                            style={{ left: drag.ghost.x, top: drag.ghost.y, height: drag.ghost.h, background: gs.bg, borderColor: gs.border, transform: 'rotate(-1.5deg) scale(1.03)', opacity: 0.9 }}>
                            <span className="font-black uppercase tracking-wider whitespace-nowrap" style={{ color: gs.color, fontSize: '10px' }}>{kindShortLabel(g.kind)}</span>
                            <span className="font-bold" style={{ color: gs.color, fontSize: '10px' }}>{g.duration_days || 1}d</span>
                            <span className="flex items-center gap-0.5 font-bold" style={{ color: gs.color, fontSize: '10px' }}><PersonIcon color={gs.color} />{g.crew_size || 0}</span>
                        </div>
                    );
                })()}
                {sorted.map((p, idx) => {
                    const style = kindChipStyle(p.kind);
                    const isMergeTarget = drag?.target?.mode === 'merge' && drag.target.idx === idx;
                    const isDragging = drag?.idx === idx;

                    return (
                        <React.Fragment key={`phase-${p.sequence}`}>
                            <div
                                ref={(el) => { if (el) chipRefs.current.set(p.sequence, el); else chipRefs.current.delete(p.sequence); }}
                                onPointerDown={(e) => handlePointerDown(e, idx)}
                                onPointerMove={handlePointerMove}
                                onPointerUp={handlePointerUp}
                                onPointerCancel={endDrag}
                                className={`
                                    flex items-center gap-1.5 pl-1.5 pr-2.5 py-2.5 rounded-lg border-2 select-none
                                    ${drag ? '' : 'hover:shadow-md'} transition-[box-shadow,opacity,transform] duration-150
                                    ${isDragging ? 'opacity-30' : ''}
                                    ${isMergeTarget ? 'ring-2 ring-indigo-500 ring-offset-1 scale-105 shadow-lg' : ''}
                                `}
                                style={{
                                    background: style.bg,
                                    borderColor: isMergeTarget ? '#6366f1' : style.border,
                                    cursor: disabled ? 'default' : (drag ? 'grabbing' : 'grab'),
                                    touchAction: disabled ? 'auto' : 'pan-y',
                                }}
                            >
                                {/* Grip — where the hand naturally goes to drag
                                    (the whole chip drags; the controls click). */}
                                {!disabled && (
                                    <svg width="7" height="12" viewBox="0 0 7 12" className="flex-shrink-0" style={{ color: style.color, opacity: 0.45 }} aria-hidden="true">
                                        {[1.5, 5.5].map(x => [1.5, 6, 10.5].map(y => <circle key={x + ':' + y} cx={x} cy={y} r="1.1" fill="currentColor" />))}
                                    </svg>
                                )}
                                {/* Kind label — click to swap the kind in place
                                    (crew/duration/date survive the swap). */}
                                <button type="button"
                                    onClick={(e) => openKindMenu(e, p.sequence)}
                                    disabled={disabled}
                                    title={disabled ? undefined : (isComboKind(p.kind) ? 'Combo day — change type or split into separate days' : 'Change phase type (keeps crew & duration)')}
                                    className={`font-black uppercase tracking-wider whitespace-nowrap py-1 ${disabled ? '' : 'hover:underline decoration-dotted underline-offset-2 cursor-pointer'}`}
                                    style={{ color: style.color, fontSize: '10px' }}>
                                    {kindShortLabel(p.kind)}{!disabled && ' ▾'}
                                </button>

                                {/* Duration stepper */}
                                <div className="flex items-center bg-white/60 rounded" onClick={e => e.stopPropagation()}>
                                    {!disabled && (
                                        <button type="button"
                                            onClick={(e) => { e.stopPropagation(); handleDurationChange(p.sequence, -1); }}
                                            className="font-bold px-1.5 py-1.5 hover:bg-white/80 rounded-l leading-none"
                                            style={{ color: style.color, fontSize: '11px' }}
                                            disabled={(p.duration_days || 1) <= 1}>
                                            −
                                        </button>
                                    )}
                                    <span className="font-bold px-0.5 min-w-[16px] text-center" style={{ color: style.color, fontSize: '10px' }}>
                                        {p.duration_days || 1}d
                                    </span>
                                    {!disabled && (
                                        <button type="button"
                                            onClick={(e) => { e.stopPropagation(); handleDurationChange(p.sequence, 1); }}
                                            className="font-bold px-1.5 py-1.5 hover:bg-white/80 rounded-r leading-none"
                                            style={{ color: style.color, fontSize: '11px' }}>
                                            +
                                        </button>
                                    )}
                                </div>

                                {/* Crew size: person icon + inline stepper
                                    (Jensen 2026-08-25 — crew had no visible
                                    way to be edited on a chip). */}
                                <div className="flex items-center bg-white/60 rounded" onClick={e => e.stopPropagation()}>
                                    {!disabled && (
                                        <button type="button"
                                            onClick={(e) => { e.stopPropagation(); handleCrewChange(p.sequence, -1); }}
                                            className="font-bold px-1.5 py-1.5 hover:bg-white/80 rounded-l leading-none"
                                            style={{ color: style.color, fontSize: '11px' }}
                                            disabled={(Number(p.crew_size) || 0) <= 0}>
                                            −
                                        </button>
                                    )}
                                    <span className="flex items-center gap-0.5 font-bold px-0.5 min-w-[20px] justify-center" style={{ color: style.color, fontSize: '10px' }}>
                                        <PersonIcon color={style.color} />
                                        {p.crew_size || 0}
                                    </span>
                                    {!disabled && (
                                        <button type="button"
                                            onClick={(e) => { e.stopPropagation(); handleCrewChange(p.sequence, 1); }}
                                            className="font-bold px-1.5 py-1.5 hover:bg-white/80 rounded-r leading-none"
                                            style={{ color: style.color, fontSize: '11px' }}>
                                            +
                                        </button>
                                    )}
                                </div>

                                {/* Delete */}
                                {!disabled && (
                                    <IconButton label="Remove phase" size="sm" tone="danger" className="ml-0.5"
                                        onClick={(e) => { e.stopPropagation(); handleRemove(p.sequence); }}>
                                        <span className="leading-none text-sm">×</span>
                                    </IconButton>
                                )}
                            </div>

                            {/* Arrow connector — clickable: combines the two
                                neighboring phases into one combo day
                                (2026-08-26). Hover turns it indigo. */}
                            {idx < sorted.length - 1 && (
                                disabled ? (
                                    <svg width="10" height="8" viewBox="0 0 10 8" className="flex-shrink-0 text-slate-300">
                                        <path d="M0 4 L6 4 M5 1.5 L7.5 4 L5 6.5" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                ) : (
                                    <button type="button"
                                        onClick={() => mergeByIndex(idx, idx + 1)}
                                        title="Combine these two phases into one day"
                                        className="flex-shrink-0 text-slate-300 hover:text-indigo-600 transition-colors px-1 py-2.5 leading-none">
                                        <svg width="10" height="8" viewBox="0 0 10 8" className="block">
                                            <path d="M0 4 L6 4 M5 1.5 L7.5 4 L5 6.5" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </button>
                                )
                            )}
                        </React.Fragment>
                    );
                })}

            </div>

            {/* Live drop hint while dragging */}
            {dragHint && (
                <div className={`text-[10px] font-semibold px-2 py-1 rounded-md ${dragHint.tone === 'merge' ? 'bg-indigo-50 text-indigo-700' : dragHint.tone === 'reorder' ? 'bg-slate-100 text-slate-700' : 'text-slate-400'}`}>
                    {dragHint.text}
                </div>
            )}

            {/* ── Kind-swap menu (portaled, same pattern as + Phase) ────── */}
            {kindMenu && createPortal(
                <div ref={kindMenuRef}
                    className="fixed bg-white rounded-lg border border-slate-200 shadow-xl py-1 w-64"
                    style={{ top: kindMenu.top, left: kindMenu.left, zIndex: 9998 }}>
                    {(() => {
                        // Combo chips open as a SEGMENT LIST (Jaylen
                        // 2026-08-26 round 2): each part can be reordered
                        // (↑/↓ — the chip label updates live) or split off
                        // into its own neighboring day one at a time; the
                        // split-all action sits below the list.
                        const menuPhase = sorted.find(p => p.sequence === kindMenu.seq);
                        if (!menuPhase || !isComboKind(menuPhase.kind)) return null;
                        const segs = kindSegments(menuPhase.kind);
                        return (
                            <div className="border-b border-slate-100 mb-1 pb-1">
                                <div className="px-3 py-1 text-[9px] font-bold text-slate-400 uppercase tracking-wider">Combo day — {segs.length} parts, in order</div>
                                {segs.map((seg, i) => {
                                    const segStyle = kindChipStyle(seg);
                                    return (
                                        <div key={seg + i} className="flex items-center gap-1.5 px-3 py-1">
                                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: segStyle.border }}/>
                                            <span className="font-bold text-[11px] flex-1" style={{ color: segStyle.color }}>{seg === 'trash_removal' ? 'TRASH' : seg.toUpperCase()}</span>
                                            <IconButton label="Move earlier in the day" size="sm" disabled={i === 0}
                                                onClick={() => reorderSegment(kindMenu.seq, i, -1)}>
                                                <span className="text-[11px] leading-none">▲</span>
                                            </IconButton>
                                            <IconButton label="Move later in the day" size="sm" disabled={i === segs.length - 1}
                                                onClick={() => reorderSegment(kindMenu.seq, i, 1)}>
                                                <span className="text-[11px] leading-none">▼</span>
                                            </IconButton>
                                            <Button variant="tertiary" size="sm"
                                                onClick={() => splitOffSegment(kindMenu.seq, i)}
                                                title={`Split ${seg.replace('_', ' ')} off into its own day`}>⑃ Split off</Button>
                                        </div>
                                    );
                                })}
                                <button type="button"
                                    onClick={() => splitPhase(kindMenu.seq)}
                                    className="w-full text-left px-3 py-1.5 hover:bg-indigo-50 text-[10px] font-bold text-indigo-600">
                                    ⑃ Split ALL into {segs.length} separate days
                                </button>
                            </div>
                        );
                    })()}
                    <div className="px-3 py-1 text-[9px] font-bold text-slate-400 uppercase tracking-wider">Change type — keeps crew &amp; days</div>
                    {PHASE_KINDS.map(k => {
                        const kStyle = kindChipStyle(k.key);
                        const current = sorted.find(p => p.sequence === kindMenu.seq)?.kind === k.key;
                        return (
                            <button key={k.key} type="button"
                                onClick={() => handleKindSwap(kindMenu.seq, k.key)}
                                className={`w-full text-left px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2 text-[11px] ${current ? 'bg-slate-50' : ''}`}>
                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: kStyle.border }}/>
                                <span className="font-bold" style={{ color: kStyle.color }}>{k.label.toUpperCase()}</span>
                                {current && <span className="ml-auto text-slate-400">✓</span>}
                            </button>
                        );
                    })}
                </div>,
                document.body
            )}

            {/* ── Empty state ───────────────────────────────────────────── */}
            {sorted.length === 0 && (
                <div className="text-slate-400 text-[10px]">
                    No phases yet — click the phase types above in trip order (e.g. PACK, LOAD, DRIVE, UNLOAD, RETURN). Drag one chip onto another to make a combo day.
                </div>
            )}
        </div>
    );
}
